import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, Table, Users, Plane } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExportCenter = () => {
  const [loading, setLoading] = useState({});

  const downloadFile = async (endpoint, filename, key) => {
    setLoading(prev => ({ ...prev, [key]: true }));
    try {
      const response = await axios.get(`${BACKEND_URL}${endpoint}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded!`);
    } catch (error) {
      toast.error('Export failed. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const dateStr = new Date().toISOString().split('T')[0];

  const exports = [
    {
      key: 'excel',
      title: 'Full Data (Excel)',
      desc: 'All Trips + Travellers in one Excel file with separate sheets. Best for detailed analysis.',
      icon: <FileSpreadsheet className="h-8 w-8" />,
      color: 'emerald',
      endpoint: '/api/exports/excel',
      filename: `travcations-data-${dateStr}.xlsx`,
      btnText: 'Download Excel',
    },
    {
      key: 'trips-csv',
      title: 'Trips (CSV)',
      desc: 'Export all trip/quote data as CSV. Compatible with all spreadsheet apps.',
      icon: <Plane className="h-8 w-8" />,
      color: 'blue',
      endpoint: '/api/exports/csv',
      filename: `travcations-trips-${dateStr}.csv`,
      btnText: 'Download Trips CSV',
    },
    {
      key: 'travellers-csv',
      title: 'Travellers (CSV)',
      desc: 'Export all traveller/client data as CSV.',
      icon: <Users className="h-8 w-8" />,
      color: 'violet',
      endpoint: '/api/exports/travellers-csv',
      filename: `travcations-travellers-${dateStr}.csv`,
      btnText: 'Download Travellers CSV',
    },
    {
      key: 'gsheets',
      title: 'Google Sheets Export',
      desc: 'Download TSV file optimized for Google Sheets import. Open Google Sheets → File → Import → Upload.',
      icon: <Table className="h-8 w-8" />,
      color: 'teal',
      endpoint: '/api/exports/google-sheets',
      filename: `travcations-data-${dateStr}.tsv`,
      btnText: 'Export for Google Sheets',
    },
  ];

  const colorMap = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', btn: 'bg-emerald-600 hover:bg-emerald-700' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', btn: 'bg-blue-600 hover:bg-blue-700' },
    violet: { bg: 'bg-violet-50', text: 'text-violet-600', btn: 'bg-violet-600 hover:bg-violet-700' },
    teal: { bg: 'bg-teal-50', text: 'text-teal-600', btn: 'bg-teal-600 hover:bg-teal-700' },
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 tracking-tight">
          Export Center
        </h1>
        <p className="text-base text-slate-600 mt-2">Download your data in Excel, CSV, or Google Sheets format</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {exports.map((item) => {
          const c = colorMap[item.color];
          return (
            <Card key={item.key} className="p-6 border border-slate-200 hover:shadow-md transition-shadow" data-testid={`export-card-${item.key}`}>
              <div className="flex items-start space-x-4">
                <div className={`${c.bg} ${c.text} p-3 rounded-lg`}>
                  {item.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-heading font-semibold text-slate-900 mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">{item.desc}</p>
                  <Button
                    onClick={() => downloadFile(item.endpoint, item.filename, item.key)}
                    disabled={loading[item.key]}
                    data-testid={`export-${item.key}-btn`}
                    className={`w-full ${c.btn} text-white`}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {loading[item.key] ? 'Exporting...' : item.btnText}
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-6 border border-slate-200">
        <h2 className="text-xl font-heading font-semibold text-slate-900 mb-3">How to Import into Google Sheets</h2>
        <ol className="space-y-2 text-sm text-slate-600 list-decimal list-inside">
          <li>Click "Export for Google Sheets" above to download the TSV file</li>
          <li>Open <a href="https://sheets.google.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline">Google Sheets</a> and create a new spreadsheet</li>
          <li>Go to <strong>File → Import → Upload</strong> and select the downloaded file</li>
          <li>Choose "Replace spreadsheet" and click "Import data"</li>
        </ol>
      </Card>
    </div>
  );
};

export default ExportCenter;
